/**
 * @fileoverview Saved-view filter <-> wire conversion (MOR-696 Stage 2)
 *
 * Split out of `saved-view-switcher.tsx` (react-refresh/only-export-components)
 * — pure conversion between the client-side `TaskFilterState` and the server
 * `taskViewFilters` shape.
 */

import type { Id } from "@cvx/_generated/dataModel";
import type { TaskViewFilters } from "@cvx/tasks/taskViews/schema";

import type { TaskFilterState } from "./task-filter-bar.helpers";
import { emptyFilters } from "./task-filter-bar.helpers";

/** Client filter state → server `taskViewFilters` (drops blanks). */
export function filtersToWire(f: TaskFilterState): TaskViewFilters {
  const wire: TaskViewFilters = {};
  if (f.statusIds.length) wire.statusIds = f.statusIds as Id<"taskStatuses">[];
  if (f.assigneeUserIds.length)
    wire.assigneeUserIds = f.assigneeUserIds as Id<"users">[];
  if (f.labelIds.length) wire.labelIds = f.labelIds as Id<"taskLabels">[];
  if (f.priorities.length) wire.priorities = f.priorities;
  if (f.categories.length) wire.categories = f.categories;
  if (f.jobStatuses.length) wire.jobStatuses = f.jobStatuses;
  if (f.overdueOnly) wire.overdueOnly = true;
  if (f.today) wire.today = true;
  if (f.hideSubtasks) wire.hideSubtasks = true;
  if (f.dueRange) {
    wire.dueRange = {
      preset: f.dueRange.preset,
      // Fixed dates only ride the custom preset; rolling presets recompute.
      ...(f.dueRange.preset === "custom" && f.dueRange.from
        ? { from: f.dueRange.from }
        : {}),
      ...(f.dueRange.preset === "custom" && f.dueRange.to
        ? { to: f.dueRange.to }
        : {}),
      ...(f.dueRange.includeOverdue ? { includeOverdue: true } : {}),
    };
  }
  if (f.search.trim()) wire.search = f.search.trim();
  return wire;
}

/** Server `taskViewFilters` → client filter state. */
export function filtersFromWire(w: TaskViewFilters): TaskFilterState {
  return {
    ...emptyFilters(),
    statusIds: (w.statusIds ?? []).map(String),
    assigneeUserIds: (w.assigneeUserIds ?? []).map(String),
    labelIds: (w.labelIds ?? []).map(String),
    priorities: w.priorities ?? [],
    // Legacy `kinds` (pre-MOR-707) is intentionally dropped: the event/task
    // split no longer exists, so old saved views just lose that constraint.
    categories: w.categories ?? [],
    jobStatuses: w.jobStatuses ?? [],
    overdueOnly: w.overdueOnly ?? false,
    today: w.today ?? false,
    hideSubtasks: w.hideSubtasks ?? false,
    dueRange: w.dueRange ?? null,
    search: w.search ?? "",
  };
}
